import { z } from "zod";

import { TransientRepositorySchema } from "./migration-instance";
import * as Butterflow from "./workflow-butterflow";

export {
  MigrationOpportunitySchema,
  MigrationOpportunityVersionSchema,
  MigrationOpportunityVulnerabilitySchema,
  MigrationOpportunityDependencyUpgradeSchema,
  MigrationOpportunityDependencyVulnerabilitySchema,
} from "./migration-opportunity";

export type { MigrationOpportunitySchemaType } from "./migration-opportunity";

export const UserGlobalRoleSchema = z.enum(["admin", "client", "internal"]);

export type UserGlobalRoleType = z.infer<typeof UserGlobalRoleSchema>;

export type CommaSeparatedRoles =
  | `${UserGlobalRoleType},${UserGlobalRoleType},${UserGlobalRoleType}`
  | `${UserGlobalRoleType},${UserGlobalRoleType}`
  | UserGlobalRoleType;

export const BuildLogSchema = z.object({
  id: z.number(),
  createdAt: z.string(),
  logSource: z.enum(["provisioner", "provisioner_daemon", "agent"]).optional(),
  logLevel: z.enum(["debug", "error", "info", "trace", "warn"]),
  stage: z.string(),
  output: z.string(),
});

export type BuildLogSchemaType = z.infer<typeof BuildLogSchema>;

export const ProjectFormSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().optional(),
});

export type ProjectFormSchemaType = z.infer<typeof ProjectFormSchema>;

export const CreateProjectFromPackageSchema = z.object({
  packageId: z.string().uuid(),
  packageVersionId: z.string().uuid(),
  projectName: z.string().min(1).max(255),
  projectDescription: z.string().optional(),
  parameters: z.record(z.unknown()).default({}),
  repositories: z
    .array(TransientRepositorySchema)
    .min(1, "At least one repository is required"),
});

export type CreateProjectFromPackageSchemaType = z.infer<
  typeof CreateProjectFromPackageSchema
>;

export const PaginationSchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(50).optional(),
});

export type PaginationSchemaType = z.infer<typeof PaginationSchema>;

export const OwnerTypeSchema = z.enum(["user", "organization"]);

export type OwnerTypeSchemaType = z.infer<typeof OwnerTypeSchema>;

export const RepoSyncBackgroundTaskPayloadSchema = z.object({
  repositoryName: z.string(),
  status: z.enum(["success", "error"]),
  total: z.number(),
  processed: z.number(),
});

export type RepoSyncBackgroundTaskPayloadSchemaType = z.infer<
  typeof RepoSyncBackgroundTaskPayloadSchema
>;

export const PositiveIntegerSchema = z.number().int().positive();

export const JoinWaitlistFormSchema = z.object({
  company: z.string(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  interest: z.string(),
  productUpdatesOptIn: z.boolean().default(true).optional(),
  indicies: z.number().array(),
});

export const WorkflowRunStatusInputSchema = z.enum([
  "running",
  "completed",
  "failed",
  "canceled",
]);

export type WorkflowRunStatusInputSchemaType = z.infer<
  typeof WorkflowRunStatusInputSchema
>;

export const AstGrepMatchSchema = z.object({
  text: z.string(),
  range: z.object({
    byteOffset: z.object({ start: z.number(), end: z.number() }),
    start: z.object({ line: z.number(), column: z.number() }),
    end: z.object({ line: z.number(), column: z.number() }),
  }),
  file: z.string(),
  lines: z.string(),
  charCount: z.object({ leading: z.number(), trailing: z.number() }),
  language: z.string(),
  ruleId: z.string(),
  severity: z.string(),
  note: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
});

export type AstGrepMatchSchemaType = z.infer<typeof AstGrepMatchSchema>;

export const AstGrepStreamMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("match"),
    match: AstGrepMatchSchema,
  }),
  z.object({
    type: z.literal("end"),
    matchCount: z.number(),
  }),
  z.object({
    type: z.literal("fileContent"),
    file: z.string(),
    content: z.string(),
  }),
  z.object({
    type: z.literal("error"),
    error: z.string().optional(),
  }),
]);

export type AstGrepStreamMessageSchemaType = z.infer<
  typeof AstGrepStreamMessageSchema
>;

export const ChartDateRangeSchema = z
  .enum(["7d", "30d", "90d", "1y"])
  .default("30d");

export type ChartDateRangeSchemaType = z.infer<typeof ChartDateRangeSchema>;

export const ChartIntervalSchema = z
  .enum(["day", "week", "month"])
  .default("day");

export type ChartIntervalSchemaType = z.infer<typeof ChartIntervalSchema>;

// AI
export * from "./ai";

// Workflow
export * from "./workflow";
export * from "./workflow-validation";

// Studio
export * from "./studio";

// Insights
export * from "./insights-dashboard";

// Migration Instance
export * from "./migration-instance";

// Git Account
export * from "./git-account";

// Registry
export * from "./registry";

export { Butterflow };

export * from "./organization";
